import {
  styled,
  Box,
  Typography,
  Button,
  TextField,
} from "@mui/material";

const PAGE_BG = "#0e3f8cff";
const CARD_BG = "#fff";
const PRIMARY = "#0972d3";
const PRIMARY_DARK = "#0865bc";
const SUCCESS_TEXT = "#2e7d32";
const HINT_TEXT = "#666";
const FOOTER_BG = "#f5f5f5";

export const StyledPage = styled(Box)({
  position: "relative",
  height: "100dvh",
  maxHeight: "100dvh",
  minHeight: "100dvh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  background: PAGE_BG,
});

/** Blue main area — signup/reset card is centered here. */
export const StyledAuthMain = styled(Box)({
  flex: "1 1 auto",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 24,
  minHeight: 0,
  overflow: "hidden",
  boxSizing: "border-box",
});

export const StyledAuthFooter = styled(Box)({
  flexShrink: 0,
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: FOOTER_BG,
  padding: "10px 16px",
  minHeight: 52,
  boxSizing: "border-box",
});

export const StyledInframarkFooterLogo = styled("img")({
  height: 40,
  width: "auto",
  maxWidth: "min(280px, 90vw)",
  objectFit: "contain",
  display: "block",
});

export const StyledContainer = styled(Box)({
  width: "100%",
  maxWidth: 580,
  maxHeight: "100%",
  minHeight: 0,
  display: "flex",
  justifyContent: "center",
});

export const StyledCard = styled(Box)({
  width: "100%",
  padding: "32px 40px",
  background: CARD_BG,
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  boxSizing: "border-box",
});

export const StyledSignupCard = styled(StyledCard)({
  height: "calc(100dvh - 52px - 3rem)",
  maxHeight: "calc(100dvh - 52px - 3rem)",
  display: "grid",
  gridTemplateRows: "auto 1fr",
  overflow: "hidden",
  alignItems: "stretch",
  padding: "24px 32px",
});

export const StyledSignupCardHeader = styled(Box)({
  flexShrink: 0,
  width: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  "& h2, & p": {
    alignSelf: "stretch",
  },
});

export const StyledWatermindsLogo = styled("img")({
  width: 160,
  maxWidth: "100%",
  height: "auto",
  marginBottom: 20,
});

export const StyledInframarkLogo = styled("img")({
  margin: "10px auto 20px auto",
  width: "50%",
  maxWidth: "100%",
});

export const StyledBackButton = styled(Button)({
  position: "absolute",
  top: 24,
  left: 24,
  minWidth: 40,
  width: 40,
  height: 40,
  color: "#fff",
  background: "rgba(255, 255, 255, 0.15)",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  borderRadius: 8,
  "&:hover": {
    background: "rgba(255, 255, 255, 0.25)",
    color: "#fff",
  },
  "&:disabled": {
    opacity: 0.5,
  },
});

export const StyledTitle = styled(Typography)({
  margin: "0 0 8px",
  fontSize: "1.75rem",
  fontWeight: 700,
  color: "#000",
  width: "100%",
  textAlign: "left",
  "&.MuiTypography-h5": {
    fontWeight: 700,
  },
});

export const StyledSubtitle = styled(Typography)({
  margin: "0 0 24px",
  fontSize: "0.95rem",
  color: HINT_TEXT,
  width: "100%",
  textAlign: "left",
  marginBottom: 16,

});

export const StyledForm = styled("form")({
  width: "100%",
});

export const StyledSignupForm = styled(StyledForm)({
  minHeight: 0,
  display: "grid",
  gridTemplateRows: "1fr auto",
  overflow: "hidden",
});

export const StyledSignupFieldsScroll = styled(Box)(({ theme }) => ({
  minHeight: 0,
  width: "100%",
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  boxSizing: "border-box",
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(2),
  paddingRight: theme.spacing(0.5),
  scrollPaddingTop: theme.spacing(2.5),
  "& .MuiFormControl-root": {
    overflow: "visible",
  },
  "& > *:first-of-type": {
    marginTop: theme.spacing(0.5),
  },
}));

export const StyledSignupFormFooter = styled(Box)({
  flexShrink: 0,
  width: "100%",
  paddingTop: 16,
});

export const StyledPasswordHint = styled(Typography)({
  margin: "0 0 8px",
  fontSize: "0.8rem",
  color: HINT_TEXT,
  fontWeight: 400,
  lineHeight: 1.35,
});

export const StyledFieldHint = styled(Typography)({
  margin: "4px 0 16px",
  fontSize: "0.8rem",
  color: HINT_TEXT,
  fontWeight: 400,
  lineHeight: 1.35,
  width: "100%",
});

export const StyledErrorMessage = styled(Typography)({
  marginBottom: 16,
  padding: 12,
  fontSize: "0.875rem",
  borderRadius: 4,
  textAlign: "center",
});

export const StyledSubmitButton = styled(Button)({
  width: "100%",
  marginTop: 16,
  padding: "14px",
  fontSize: "1rem",
  fontWeight: 500,
  color: "#fff",
  background: PRIMARY,
  textTransform: "none",
  "&:hover": {
    background: PRIMARY_DARK,
  },
  "&:disabled": {
    opacity: 0.6,
  },
});

export const StyledTextField = styled(TextField)({
  width: "100%",
  marginBottom: 20,
  "& .MuiOutlinedInput-root": {
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: PRIMARY_DARK,
      borderWidth: 1,
    },
  },
});

export const StyledSecondaryButton = styled(Button)({
  width: "100%",
  marginTop: 16,
  textTransform: "none",
});

export const StyledSuccessMessage = styled(Typography)({
  margin: "0 0 20px",
  color: SUCCESS_TEXT,
});

export const StyledPhoneFieldWrapper = styled(Box)({
  width: "100%",
  marginBottom: 20,
});

export const SignupNameFieldsRow = styled(Box)({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 16,
  width: "100%",
  alignItems: "start",
});
